import { useRouter } from "next/router"
import { useState, useCallback, useEffect } from "react"
import { useForm } from "react-hook-form"
import { FieldErrors, FieldValues } from "react-hook-form"

import { fetchPostAuthRegisterAdditionally } from "~/api/auth/register-additionally/post"
import { useComputedIfNotNull } from "~/hooks/useComputedIfNotNull"
import { internalLink } from "~/services/helper"
import { useStores } from "~/stores/StoreProvider"
import { removeObjectBy, isEmptyValue } from "~/utils"

import { HackedPostAuthRegisterBody } from "../AuthRegisterPage/types"
import { useAuthRegisterRouterQuery } from "../AuthRegisterPage/useAuthRegisterRouterQuery"
import { DATA_LAYER_EVENT } from "../InputProfileFlowPages/consts/gtm"
import type { AuthRegisterCompletePageProps } from "./index"

export const useAuthRegisterCompletePage = ({ onSubmitForMock }: AuthRegisterCompletePageProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>()
  const [isOpenDialog, setIsOpenDialog] = useState(false)

  const { userStore } = useStores()
  const router = useRouter()
  const query = useAuthRegisterRouterQuery()

  const {
    handleSubmit,
    watch,
    control,
    formState: { isDirty },
  } = useForm<HackedPostAuthRegisterBody>()

  useEffect(() => {
    if (!userStore.requestedFetchGetMe) {
      return
    }
    setIsLoggedIn(userStore.authed)
  }, [userStore.requestedFetchGetMe])

  const onBeforeSubmit = useCallback(() => {
    setErrorMessages([])
  }, [])

  /**
   * handleSubmitでinValidになった項目のエラーメッセージをまとめて登録する。errors[key]['message']で取得
   * onSubmitでerrorになった項目のエラーメッセージをまとめて登録する。errors[key]で取得
   */
  const handleSubmitError = (errors: FieldErrors<FieldValues>) => {
    const messages = Object.entries(errors)
      .flatMap(([, fieldError]): Array<string | undefined> => {
        if (Array.isArray(fieldError)) {
          return fieldError.map((field) => field.message || field)
        } else {
          return [fieldError?.message || fieldError]
        }
      })
      .filter((it): it is string => it != null)
    setErrorMessages(messages)
  }

  /**
   * 送信処理
   */
  const onSubmit = handleSubmit(async (body) => {
    setIsLoading(true)

    // storybook only
    if (onSubmitForMock) {
      onSubmitForMock(body)
      setIsLoading(false)
      return
    }

    const response = await fetchPostAuthRegisterAdditionally(
      removeObjectBy(body, isEmptyValue) as HackedPostAuthRegisterBody
    )

    if (response.error && response.errors !== undefined) {
      handleSubmitError(response.errors)
      window.scrollTo({ top: 0, behavior: "smooth" })
      setIsLoading(false)
      return
    }

    window.dataLayer.push({ event: DATA_LAYER_EVENT.COMPLETE_NEW_REGISTRATION_ADDITIONALLY })
    onNavigate()
  }, handleSubmitError)

  const { isShownNewGradJobs, isShownLongInternship, isShownExperience } = useComputedIfNotNull(
    watch("application_category_ids"),
    (ids) => ({
      isShownNewGradJobs: ids.includes("3"),
      isShownLongInternship: ids.includes("1"),
      isShownExperience: ids.length !== 0,
    })
  )

  const onSkip = () => {
    if (isDirty) {
      setIsOpenDialog(true)
    } else {
      onNavigate()
    }
  }

  const onNavigate = () => {
    router.replace({
      pathname: internalLink("/auth/register/complete-additionally"),
      query,
    })
  }

  return {
    control,
    errorMessages,
    isLoading,
    isLoggedIn,
    isOpenDialog,
    isShownExperience,
    isShownLongInternship,
    isShownNewGradJobs,
    onBeforeSubmit,
    onNavigate,
    onSkip,
    onSubmit,
    setIsOpenDialog,
  }
}
